import { useState, useEffect } from "react";
import {
  FormControl,
  Select as MuiSelect,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  IconButton,
  Tooltip,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import AnchorIcon from "@mui/icons-material/Anchor";
import { useDispatch, useSelector } from "react-redux";
import { setFilters } from "../store/slices/filterSlice";

const INPUT_HEIGHT = 40;
const getLocalDateString = (d = new Date()) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const today = getLocalDateString();
const selectSx = (width = 200, enabled = true) => ({
  width,
  height: INPUT_HEIGHT,
  borderRadius: "9px",
  fontSize: "13px",
  fontWeight: 500,
  background: enabled ? "#fff" : "#f8fafc",
  opacity: enabled ? 1 : 0.5,
  transition: "all 0.2s",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#e2e8f0",
    borderWidth: "1.5px",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: enabled ? "#3b82f6" : "#e2e8f0",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#3b82f6",
    borderWidth: "1.5px",
  },
  "& .MuiSelect-select": {
    padding: "0 12px",
    height: `${INPUT_HEIGHT}px !important`,
    display: "flex",
    alignItems: "center",
    color: enabled ? "#1e293b" : "#94a3b8",
    boxSizing: "border-box",
  },
});

const dateInputStyle = () => ({
  border: "1.5px solid #e2e8f0",
  borderRadius: 9,
  height: INPUT_HEIGHT,
  padding: "0 12px",
  fontSize: 13,
  fontWeight: 500,
  color: "#1e293b",
  background: "#fff",
  fontFamily: "inherit",
  outline: "none",
  cursor: "text",
  width: 148,
  boxSizing: "border-box",
  transition: "border-color 0.18s",
});

const FieldLabel = ({ children }) => (
  <p
    style={{
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: "#64748b",
      marginBottom: 6,
      whiteSpace: "nowrap",
      display: "flex",
      alignItems: "center",
      gap: 4,
    }}
  >
    {children}
  </p>
);

const VDivider = () => (
  <div
    style={{
      width: 1,
      height: INPUT_HEIGHT,
      background: "#e2e8f0",
      flexShrink: 0,
      alignSelf: "flex-end",
      margin: "0 20px",
    }}
  />
);

const RadioToggle = ({ active, onClick }) => (
  <div
    onClick={onClick}
    style={{
      display: "flex",
      alignItems: "center",
      cursor: "pointer",
      userSelect: "none",
      height: INPUT_HEIGHT,
    }}
  >
    <div
      style={{
        width: 18,
        height: 18,
        borderRadius: "50%",
        flexShrink: 0,
        border: `2px solid ${active ? "#3b82f6" : "#cbd5e1"}`,
        background: active ? "#3b82f6" : "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "all 0.18s",
        boxShadow: active ? "0 0 0 3px rgba(59,130,246,0.15)" : "none",
      }}
    >
      {active && (
        <div
          style={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            background: "#fff",
          }}
        />
      )}
    </div>
  </div>
);

const ModePill = ({ active, onClick, icon, label }) => (
  <div
    onClick={onClick}
    style={{
      display: "flex",
      alignItems: "center",
      gap: 7,
      padding: "0 14px",
      height: INPUT_HEIGHT,
      borderRadius: 9,
      cursor: "pointer",
      border: `1.5px solid ${active ? "#3b82f6" : "#e2e8f0"}`,
      background: active ? "#eff6ff" : "#f8fafc",
      color: active ? "#2563eb" : "#94a3b8",
      fontSize: 13,
      fontWeight: active ? 600 : 400,
      transition: "all 0.2s",
      userSelect: "none",
      whiteSpace: "nowrap",
      boxShadow: active ? "0 2px 8px rgba(59,130,246,0.15)" : "none",
      boxSizing: "border-box",
    }}
  >
    <div
      style={{
        width: 14,
        height: 14,
        borderRadius: "50%",
        border: `2px solid ${active ? "#3b82f6" : "#cbd5e1"}`,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "all 0.15s",
      }}
    >
      {active && (
        <div
          style={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: "#3b82f6",
          }}
        />
      )}
    </div>
    <span style={{ fontSize: 14 }}>{icon}</span>
    {label}
  </div>
);

// ✅ Removed: companies state, selectedCompanyId, getAllCompanies import,
//            VISO_SUPER_ADMIN import, renderCompanyDropdown, onCompanyChange prop
//    All company switching is now handled by CompanyHeader in App.jsx

const HeaderForm = ({
  yardOptions,
  shippingLineOptions,
  allShippingLines = [],
  onSubmit,
  isFilterMode = false,
  dropDownLabel,
}) => {
  const dispatch = useDispatch();
  const filters = useSelector((state) => state.filters);

  const [useDateFilter, setUseDateFilter] = useState(true);
  const [useDaysFilter, setUseDaysFilter] = useState(false);
  const [useShippingLineFilter, setUseShippingLineFilter] = useState(false);
  const [selectedYards, setSelectedYards] = useState([]);
  const [selectedShippingLines, setSelectedShippingLines] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [days, setDays] = useState("90");

  const availableShippingLines = selectedYards.length
    ? allShippingLines
        .filter((sl) => selectedYards.includes(sl.yard_id))
        .map((sl) => ({
          label: sl.shipping_line_name,
          value: sl.shipping_line_id,
        }))
    : shippingLineOptions;

  useEffect(() => {
    if (!yardOptions?.length) return;
    const allYards = yardOptions.map((o) => o.value);
    const allShippingLineValues =
      shippingLineOptions?.map((o) => o.value) || [];
    const now = new Date(),
      start = new Date();
    start.setDate(now.getDate() - 6);

    setSelectedYards(filters.yards?.length ? filters.yards : allYards);
    if (selectedShippingLines.length === 0)
      setSelectedShippingLines(
        filters.shippingLines?.length
          ? filters.shippingLines
          : allShippingLineValues,
      );
    if (!startDate)
      setStartDate(filters.startDate || getLocalDateString(start));
    if (!endDate) {
      const persisted = filters.endDate;
      setEndDate(
        persisted && persisted >= today ? persisted : getLocalDateString(now),
      );
    }
    if (isFilterMode) {
      setUseDateFilter(filters.filterMode !== "days");
      setUseDaysFilter(filters.filterMode === "days");
      setUseShippingLineFilter(filters.useShippingLineFilter || false);
    }
  }, [yardOptions]);

  const handleYardChange = (newYards) => {
    setSelectedYards(newYards);
    const validForNewYards = allShippingLines
      .filter((sl) => newYards.includes(sl.yard_id))
      .map((sl) => sl.shipping_line_id);
    const cleaned = selectedShippingLines.filter((id) =>
      validForNewYards.includes(id),
    );
    setSelectedShippingLines(cleaned);
  };

  const handleSubmit = () => {
    const payload = { yards: selectedYards };
    if (useShippingLineFilter) payload.shippingLines = selectedShippingLines;

    if (isFilterMode && useDaysFilter) {
      payload.days = days;
      dispatch(
        setFilters({
          ...filters,
          yards: selectedYards,
          shippingLines: useShippingLineFilter ? selectedShippingLines : [],
          days,
          filterMode: "days",
          useShippingLineFilter,
        }),
      );
    } else {
      payload.startDate = startDate;
      payload.endDate = endDate;
      dispatch(
        setFilters({
          ...filters,
          yards: selectedYards,
          shippingLines: useShippingLineFilter ? selectedShippingLines : [],
          startDate,
          endDate,
          filterMode: "date",
          useShippingLineFilter,
        }),
      );
    }
    onSubmit(payload);
  };

  const handleStartDateChange = (e) => {
    const value = e.target.value;
    if (!value) return;
    if (endDate && value > endDate) return;
    setStartDate(value);
  };

  const handleEndDateChange = (e) => {
    const value = e.target.value;
    if (!value) return;
    if (value > today) return;
    if (startDate && value < startDate) return;
    setEndDate(value);
  };

  const wrapperStyle = {
    background: "#fff",
    borderRadius: 16,
    border: "1.5px solid #e2e8f0",
    boxShadow: "0 2px 14px rgba(15,28,53,0.07)",
    overflow: "hidden",
    fontFamily: "'DM Sans','Inter',sans-serif",
  };

  const rowStyle = (isSecondRow = false) => ({
    display: "flex",
    alignItems: "flex-end",
    flexWrap: "wrap",
    padding: isSecondRow ? "12px 24px 18px" : "18px 24px 16px",
    ...(isSecondRow && {
      gap: 16,
      borderTop: "1px solid #f1f5f9",
      background: "#fafbfd",
    }),
  });

  // ── HOME MODE ─────────────────────────────────────────────────────────────
  if (!isFilterMode) {
    return (
      <div style={wrapperStyle}>
        <div style={{ ...rowStyle(), gap: 20 }}>
          <div style={{ flexShrink: 0 }}>
            <FieldLabel>📍 Yard</FieldLabel>
            <FormControl size="small">
              <MuiSelect
                multiple
                value={selectedYards}
                onChange={(e) => handleYardChange(e.target.value)}
                input={<OutlinedInput />}
                renderValue={(sel) =>
                  sel.length > 1 && sel.length === yardOptions.length
                    ? "All Yards"
                    : sel
                        .map(
                          (v) => yardOptions.find((o) => o.value === v)?.label,
                        )
                        .join(", ")
                }
                sx={selectSx(280)}
              >
                {yardOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    <Checkbox
                      checked={selectedYards.includes(o.value)}
                      size="small"
                      sx={{ "&.Mui-checked": { color: "#3b82f6" } }}
                    />
                    <ListItemText primary={o.label} />
                  </MenuItem>
                ))}
              </MuiSelect>
            </FormControl>
          </div>

          <div style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
            <div>
              <FieldLabel>📅 Date Range</FieldLabel>
              <input
                type="date"
                value={startDate}
                max={endDate || today}
                onChange={handleStartDateChange}
                style={dateInputStyle()}
              />
            </div>
            <span
              style={{
                color: "#cbd5e1",
                fontSize: 18,
                height: INPUT_HEIGHT,
                display: "flex",
                alignItems: "center",
                alignSelf: "flex-end",
              }}
            >
              →
            </span>
            <div>
              <FieldLabel>
                <span style={{ opacity: 0 }}>_</span>
              </FieldLabel>
              <input
                type="date"
                value={endDate}
                min={startDate}
                max={today}
                onChange={handleEndDateChange}
                style={dateInputStyle()}
              />
            </div>
          </div>

          <div style={{ marginLeft: "auto" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button
                type="button"
                onClick={handleSubmit}
                style={{
                  background: "linear-gradient(135deg, #3182ce, #2b6cb0)",
                  color: "#fff",
                  border: "none",
                  borderRadius: 9,
                  height: INPUT_HEIGHT,
                  padding: "0 22px",
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  boxShadow: "0 4px 12px rgba(59,130,246,0.28)",
                  transition: "opacity 0.18s, transform 0.18s",
                  boxSizing: "border-box",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.opacity = "0.88";
                  e.currentTarget.style.transform = "translateY(-1px)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.opacity = "1";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                Apply Filters
              </button>
              <Tooltip title="Refresh">
                <IconButton
                  onClick={() => window.location.reload()}
                  sx={{
                    border: "1.5px solid #e2e8f0",
                    borderRadius: "9px",
                    width: INPUT_HEIGHT,
                    height: INPUT_HEIGHT,
                    color: "#94a3b8",
                    padding: 0,
                    "&:hover": {
                      borderColor: "#3b82f6",
                      color: "#3b82f6",
                      background: "#eff6ff",
                    },
                  }}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── FILTER MODE ───────────────────────────────────────────────────────────
  return (
    <div style={wrapperStyle}>
      <div style={rowStyle()}>
        <div style={{ flexShrink: 0 }}>
          <FieldLabel>📍 Yard</FieldLabel>
          <FormControl size="small">
            <MuiSelect
              multiple
              value={selectedYards}
              onChange={(e) => handleYardChange(e.target.value)}
              input={<OutlinedInput />}
              renderValue={(sel) =>
                sel.length > 1 && sel.length === yardOptions.length
                  ? "All Yards"
                  : sel
                      .map((v) => yardOptions.find((o) => o.value === v)?.label)
                      .join(", ")
              }
              sx={selectSx(280)}
            >
              {yardOptions.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  <Checkbox
                    checked={selectedYards.includes(o.value)}
                    size="small"
                    sx={{ "&.Mui-checked": { color: "#3b82f6" } }}
                  />
                  <ListItemText primary={o.label} />
                </MenuItem>
              ))}
            </MuiSelect>
          </FormControl>
        </div>

        <VDivider />

        <div style={{ flexShrink: 0 }}>
          <FieldLabel>
            <AnchorIcon sx={{ fontSize: 11, color: "#64748b" }} />
            Shipping Line
          </FieldLabel>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <RadioToggle
              active={useShippingLineFilter}
              onClick={() => setUseShippingLineFilter((v) => !v)}
            />
            <FormControl size="small" disabled={!useShippingLineFilter}>
              <MuiSelect
                multiple
                value={selectedShippingLines}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.includes("all")) {
                    const allIds = availableShippingLines.map((o) => o.value);
                    setSelectedShippingLines(
                      selectedShippingLines.length === allIds.length
                        ? []
                        : allIds,
                    );
                  } else {
                    setSelectedShippingLines(value);
                  }
                }}
                displayEmpty
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 250,
                      overflowY: "scroll",
                      "&::-webkit-scrollbar": { width: "8px" },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "#cbd5e1",
                        borderRadius: "6px",
                      },
                      "&::-webkit-scrollbar-track": {
                        backgroundColor: "#f1f5f9",
                      },
                    },
                  },
                  anchorOrigin: { vertical: "bottom", horizontal: "left" },
                  transformOrigin: { vertical: "top", horizontal: "left" },
                  autoFocus: false,
                  disableAutoFocusItem: true,
                }}
                renderValue={(sel) => {
                  if (!useShippingLineFilter)
                    return (
                      <span style={{ color: "#cbd5e1" }}>
                        Select Shipping Line
                      </span>
                    );
                  if (!sel.length)
                    return (
                      <span style={{ color: "#94a3b8" }}>
                        Select Shipping Line
                      </span>
                    );
                  return sel
                    .map(
                      (v) =>
                        availableShippingLines?.find((o) => o.value === v)
                          ?.label || "",
                    )
                    .filter(Boolean)
                    .join(", ");
                }}
                sx={selectSx(185, useShippingLineFilter)}
              >
                <MenuItem value="all">
                  <Checkbox
                    checked={
                      availableShippingLines.length > 0 &&
                      selectedShippingLines.length ===
                        availableShippingLines.length
                    }
                    indeterminate={
                      selectedShippingLines.length > 0 &&
                      selectedShippingLines.length <
                        availableShippingLines.length
                    }
                    size="small"
                  />
                  <ListItemText primary="Select All" />
                </MenuItem>
                {availableShippingLines?.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    <Checkbox
                      checked={selectedShippingLines.includes(o.value)}
                      size="small"
                    />
                    <ListItemText primary={o.label} />
                  </MenuItem>
                ))}
              </MuiSelect>
            </FormControl>
          </div>
        </div>
        <VDivider />
        <div style={{ flexShrink: 0 }}>
          <FieldLabel>Filter By</FieldLabel>
          <div style={{ display: "flex", gap: 8 }}>
            <ModePill
              active={useDateFilter}
              onClick={() => {
                setUseDateFilter(true);
                setUseDaysFilter(false);
              }}
              icon="📅"
              label="Date Range"
            />
            <ModePill
              active={useDaysFilter}
              onClick={() => {
                setUseDaysFilter(true);
                setUseDateFilter(false);
              }}
              icon="📦"
              label="Days at Yard"
            />
          </div>
        </div>
      </div>

      <div style={rowStyle(true)}>
        {useDateFilter && (
          <div style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
            <div>
              <FieldLabel>From</FieldLabel>
              <input
                type="date"
                value={startDate}
                max={endDate || today}
                onChange={handleStartDateChange}
                style={dateInputStyle()}
              />
            </div>
            <span
              style={{
                color: "#cbd5e1",
                fontSize: 18,
                height: INPUT_HEIGHT,
                display: "flex",
                alignItems: "center",
                alignSelf: "flex-end",
              }}
            >
              →
            </span>
            <div>
              <FieldLabel>To</FieldLabel>
              <input
                type="date"
                value={endDate}
                min={startDate}
                max={today}
                onChange={handleEndDateChange}
                style={dateInputStyle()}
              />
            </div>
          </div>
        )}

        {useDaysFilter && (
          <div>
            <FieldLabel>📦 {dropDownLabel}</FieldLabel>
            <FormControl size="small">
              <MuiSelect
                value={days}
                onChange={(e) => setDays(e.target.value)}
                sx={selectSx(180)}
              >
                <MenuItem value="90">90 Days +</MenuItem>
                <MenuItem value="120">120 Days +</MenuItem>
                <MenuItem value="150">150 Days +</MenuItem>
                <MenuItem value="180">180 Days +</MenuItem>
              </MuiSelect>
            </FormControl>
          </div>
        )}

        <div style={{ marginLeft: "auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button
              type="button"
              onClick={handleSubmit}
              style={{
                background: "linear-gradient(135deg, #3182ce, #2b6cb0)",
                color: "#fff",
                border: "none",
                borderRadius: 9,
                height: INPUT_HEIGHT,
                padding: "0 22px",
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer",
                whiteSpace: "nowrap",
                boxShadow: "0 4px 12px rgba(59,130,246,0.28)",
                transition: "opacity 0.18s, transform 0.18s",
                boxSizing: "border-box",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.opacity = "0.88";
                e.currentTarget.style.transform = "translateY(-1px)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.opacity = "1";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              Apply Filters
            </button>
            <Tooltip title="Refresh">
              <IconButton
                onClick={() => window.location.reload()}
                sx={{
                  border: "1.5px solid #e2e8f0",
                  borderRadius: "9px",
                  width: INPUT_HEIGHT,
                  height: INPUT_HEIGHT,
                  color: "#94a3b8",
                  padding: 0,
                  "&:hover": {
                    borderColor: "#3b82f6",
                    color: "#3b82f6",
                    background: "#eff6ff",
                  },
                }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeaderForm;
